import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  tasksApi,
  projectsApi,
  habitsApi,
  labelsApi,
  taskStatusesApi,
  customFieldsApi,
  settingsApi,
  notificationsApi,
  documentsApi,
  databasesApi,
  meetingsApi,
  teamsApi,
  focusTimeApi,
  trashApi,
  webhooksApi,
  taskTypesApi,
  gmailApi,
  adminApi,
  billingApi,
  googleCalendarApi,
  mcpTokensApi,
  activityApi,
  unreadsApi,
  botsApi,
  spotlightApi,
} from '../services/api';
import {
  taskKeys,
  projectKeys,
  habitKeys,
  labelKeys,
  customStatusKeys,
  customFieldKeys,
  settingsKeys,
  notificationKeys,
  documentKeys,
  databaseKeys,
  meetingKeys,
  teamKeys,
  focusTimeKeys,
  trashKeys,
  webhookKeys,
  taskTypeKeys,
  gmailKeys,
  adminKeys,
  billingKeys,
  googleCalendarKeys,
  mcpTokenKeys,
  unreadKeys,
  botKeys,
} from '../lib/queryKeys';

const unwrap = (p) => p.then((r) => r.data);

// ── Tasks ─────────────────────────────────────────────────────────────────

export const useTasksQuery = (filters = {}, options = {}) =>
  useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => unwrap(tasksApi.getAll(filters)),
    ...options,
  });

// Upcoming occurrences of recurring tasks between range.start and range.end (ISO strings).
export const useTaskOccurrencesQuery = (range, options = {}) =>
  useQuery({
    queryKey: taskKeys.occurrences(range || {}),
    queryFn: () => unwrap(tasksApi.getOccurrences(range)).then((d) => d.occurrences || []),
    enabled: !!(range?.start && range?.end),
    staleTime: 60_000,
    ...options,
  });

export const useTaskQuery = (id, options = {}) =>
  useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => unwrap(tasksApi.getById(id)),
    enabled: !!id,
    ...options,
  });

export const useTaskCommentsQuery = (taskId, options = {}) =>
  useQuery({
    queryKey: taskKeys.comments(taskId),
    queryFn: () => unwrap(tasksApi.getComments(taskId)),
    enabled: !!taskId,
    ...options,
  });

// ── Projects ──────────────────────────────────────────────────────────────

export const useProjectsQuery = (options = {}) =>
  useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => unwrap(projectsApi.getAll()),
    ...options,
  });

export const useProjectQuery = (id, options = {}) =>
  useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => unwrap(projectsApi.getById(id)),
    enabled: !!id,
    ...options,
  });

export const useProjectSectionsQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: projectKeys.sections(projectId),
    queryFn: () => unwrap(projectsApi.getSections(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useProjectActivityQuery = (projectId, params = {}, options = {}) =>
  useQuery({
    queryKey: projectKeys.activity(projectId, params),
    queryFn: () => unwrap(activityApi.getProjectActivity(projectId, params)),
    enabled: !!projectId,
    ...options,
  });

// ── Habits ────────────────────────────────────────────────────────────────

export const useHabitsQuery = (options = {}) =>
  useQuery({
    queryKey: habitKeys.list(),
    queryFn: () => unwrap(habitsApi.getAll()),
    ...options,
  });

export const useHabitScheduledEventsQuery = (options = {}) =>
  useQuery({
    queryKey: habitKeys.scheduledEvents(),
    queryFn: () => unwrap(habitsApi.getScheduledEvents()),
    ...options,
  });

// ── Labels & taxonomies ───────────────────────────────────────────────────

export const useLabelsQuery = (options = {}) =>
  useQuery({
    queryKey: labelKeys.lists(),
    queryFn: () => unwrap(labelsApi.getAll()),
    ...options,
  });

export const useCustomStatusesQuery = (teamId = null, options = {}) =>
  useQuery({
    queryKey: customStatusKeys.list(teamId),
    queryFn: () => taskStatusesApi.getAll(teamId),
    ...options,
  });

export const useCustomFieldsQuery = (projectId = null, options = {}) =>
  useQuery({
    queryKey: customFieldKeys.list(projectId),
    queryFn: () => unwrap(customFieldsApi.getAll(projectId)),
    ...options,
  });

export const useTaskTypesQuery = (options = {}) =>
  useQuery({
    queryKey: taskTypeKeys.lists(),
    queryFn: () => unwrap(taskTypesApi.getAll()),
    ...options,
  });

// ── Settings & analytics ──────────────────────────────────────────────────

export const useSettingsQuery = (options = {}) =>
  useQuery({
    queryKey: settingsKeys.current(),
    queryFn: () => unwrap(settingsApi.get()),
    ...options,
  });

export const useAnalyticsQuery = (options = {}) =>
  useQuery({
    queryKey: settingsKeys.analytics(),
    queryFn: () => unwrap(settingsApi.getAnalytics()),
    ...options,
  });

export const useTimeBreakdownQuery = (options = {}) =>
  useQuery({
    queryKey: settingsKeys.timeBreakdown(),
    queryFn: () => unwrap(settingsApi.getTimeBreakdown()),
    ...options,
  });

// ── Focus time ────────────────────────────────────────────────────────────

export const useFocusTimeQuery = (options = {}) =>
  useQuery({
    queryKey: focusTimeKeys.current(),
    queryFn: () => unwrap(focusTimeApi.get()),
    ...options,
  });

export const useFocusScheduledEventsQuery = (options = {}) =>
  useQuery({
    queryKey: focusTimeKeys.scheduledEvents(),
    queryFn: () => unwrap(focusTimeApi.getScheduledEvents()),
    ...options,
  });

// ── Notifications ─────────────────────────────────────────────────────────

export const useNotificationsQuery = (params = {}, options = {}) =>
  useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => unwrap(notificationsApi.getAll(params)),
    ...options,
  });

export const useNotificationSettingsQuery = (options = {}) =>
  useQuery({
    queryKey: notificationKeys.settings(),
    queryFn: () => unwrap(notificationsApi.getSettings()),
    ...options,
  });

// Every unread indicator for the current user in one entry. Short staleTime
// because freshness is what the indicator is judged on; the backend also
// pushes an SSE invalidate on every change, so this is a floor rather than
// the primary refresh mechanism.
export const useUnreadsQuery = (options = {}) =>
  useQuery({
    queryKey: unreadKeys.summary(),
    queryFn: () => unwrap(unreadsApi.get()),
    staleTime: 30_000,
    ...options,
  });

// ── Documents ─────────────────────────────────────────────────────────────

export const useDocumentsQuery = (filters = {}, options = {}) =>
  useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: () => unwrap(documentsApi.getAll(filters)),
    ...options,
  });

export const useDocumentQuery = (id, options = {}) =>
  useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => unwrap(documentsApi.getById(id)),
    enabled: !!id,
    ...options,
  });

// ── Databases (tabular type) ──────────────────────────────────────────────

export const useDatabaseFieldsQuery = (documentId, options = {}) =>
  useQuery({
    queryKey: databaseKeys.fields(documentId),
    queryFn: () => unwrap(databasesApi.listFields(documentId)),
    enabled: !!documentId,
    ...options,
  });

export const useDatabaseRowsQuery = (documentId, options = {}) =>
  useQuery({
    queryKey: databaseKeys.rows(documentId),
    queryFn: () => unwrap(databasesApi.listRows(documentId)),
    enabled: !!documentId,
    ...options,
  });

// ── Meetings ──────────────────────────────────────────────────────────────

export const useMeetingsQuery = (options = {}) =>
  useQuery({
    queryKey: meetingKeys.lists(),
    queryFn: () => unwrap(meetingsApi.getAll()),
    ...options,
  });

// ── Teams ─────────────────────────────────────────────────────────────────

export const useTeamsQuery = (options = {}) =>
  useQuery({
    queryKey: teamKeys.lists(),
    queryFn: () => unwrap(teamsApi.getAll()),
    ...options,
  });

export const useTeamQuery = (id, options = {}) =>
  useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => unwrap(teamsApi.getById(id)),
    enabled: !!id,
    ...options,
  });

export const useTeamInvitesQuery = (teamId, options = {}) =>
  useQuery({
    queryKey: teamKeys.invites(teamId),
    queryFn: () => unwrap(teamsApi.getInvites(teamId)),
    enabled: !!teamId,
    ...options,
  });

export const useTeamTasksQuery = (teamId, options = {}) =>
  useQuery({
    queryKey: teamKeys.tasks(teamId),
    queryFn: () => unwrap(teamsApi.getTasks(teamId)),
    enabled: !!teamId,
    ...options,
  });

// ── Trash ─────────────────────────────────────────────────────────────────

export const useTrashQuery = (options = {}) =>
  useQuery({
    queryKey: trashKeys.lists(),
    queryFn: () => unwrap(trashApi.getAll()),
    ...options,
  });

export const useTrashCountQuery = (options = {}) =>
  useQuery({
    queryKey: trashKeys.count(),
    queryFn: () => unwrap(trashApi.getCount()),
    ...options,
  });

// ── Webhooks ──────────────────────────────────────────────────────────────

export const useWebhooksQuery = (options = {}) =>
  useQuery({
    queryKey: webhookKeys.lists(),
    queryFn: () => unwrap(webhooksApi.getAll()),
    ...options,
  });

export const useWebhookEventsQuery = (id, limit = 50, options = {}) =>
  useQuery({
    queryKey: webhookKeys.events(id),
    queryFn: () => unwrap(webhooksApi.getEvents(id, limit)),
    enabled: !!id,
    // Deliveries happen in the background (and retry), so an open history
    // keeps itself current instead of showing the first load forever.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 10_000,
    ...options,
  });

// ── Gmail ─────────────────────────────────────────────────────────────────

export const useGmailStatusQuery = (options = {}) =>
  useQuery({
    queryKey: gmailKeys.status(),
    queryFn: () => unwrap(gmailApi.getStatus()),
    ...options,
  });

export const useGmailMessagesQuery = (params = {}, options = {}) =>
  useQuery({
    queryKey: gmailKeys.messages(params),
    queryFn: () => unwrap(gmailApi.getMessages(params)),
    ...options,
  });

export const useGmailMessageQuery = (id, options = {}) =>
  useQuery({
    queryKey: gmailKeys.message(id),
    queryFn: () => unwrap(gmailApi.getMessage(id)),
    enabled: !!id,
    ...options,
  });

export const useGmailThreadsQuery = (options = {}) =>
  useQuery({
    queryKey: gmailKeys.threads(),
    queryFn: () => unwrap(gmailApi.getThreads()),
    ...options,
  });

export const useGmailRulesQuery = (options = {}) =>
  useQuery({
    queryKey: gmailKeys.rules(),
    queryFn: () => unwrap(gmailApi.getRules()),
    ...options,
  });

// ── Admin ─────────────────────────────────────────────────────────────────

export const useAdminUsersQuery = (params = {}, options = {}) =>
  useQuery({
    queryKey: adminKeys.users(params),
    queryFn: () => unwrap(adminApi.getUsers(params)),
    placeholderData: (prev) => prev,  // keep the table while the next page or filter loads
    ...options,
  });

export const useAdminUserQuery = (userId, options = {}) =>
  useQuery({ queryKey: adminKeys.user(userId), queryFn: () => unwrap(adminApi.getUser(userId)), enabled: !!userId, ...options });

export const useAdminOverviewQuery = (days, options = {}) =>
  useQuery({ queryKey: adminKeys.overview(days), queryFn: () => unwrap(adminApi.overview(days)), ...options });

export const useAdminAuditQuery = (options = {}) =>
  useQuery({ queryKey: adminKeys.audit(), queryFn: () => unwrap(adminApi.audit()), ...options });

// ── Billing ───────────────────────────────────────────────────────────────

export const useBillingInvoicesQuery = (options = {}) =>
  useQuery({
    queryKey: [...billingKeys.subscription(), 'invoices'],
    queryFn: () => unwrap(billingApi.invoices()),
    ...options,
  });

export const useAdminBillingInvoicesQuery = (options = {}) =>
  useQuery({ queryKey: ['admin', 'billing-invoices'], queryFn: () => unwrap(adminApi.billingInvoices()), ...options });

export const useBillingSubscriptionQuery = (options = {}) =>
  useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => unwrap(billingApi.getSubscription()),
    ...options,
  });

// ── Google Calendar ───────────────────────────────────────────────────────

export const useGoogleCalendarStatusQuery = (options = {}) =>
  useQuery({
    queryKey: googleCalendarKeys.status(),
    queryFn: () => unwrap(googleCalendarApi.getStatus()),
    ...options,
  });

export const useGoogleCalendarCalendarsQuery = (options = {}) =>
  useQuery({
    queryKey: googleCalendarKeys.calendars(),
    queryFn: () => unwrap(googleCalendarApi.getCalendars()),
    ...options,
  });

export const useGoogleCalendarEventsQuery = (start, end, options = {}) =>
  useQuery({
    queryKey: googleCalendarKeys.events({ start, end }),
    queryFn: () => unwrap(googleCalendarApi.getEvents(start, end)),
    enabled: !!start && !!end,
    ...options,
  });

// ── MCP tokens ────────────────────────────────────────────────────────────

export const useMcpTokensQuery = (options = {}) =>
  useQuery({
    queryKey: mcpTokenKeys.lists(),
    queryFn: () => unwrap(mcpTokensApi.list()),
    ...options,
  });

/** Apps connected by signing in (OAuth), e.g. Claude or ChatGPT. */
export const useMcpConnectionsQuery = (options = {}) =>
  useQuery({
    queryKey: mcpTokenKeys.connections(),
    queryFn: () => unwrap(mcpTokensApi.connections()),
    ...options,
  });

// ── Bots ──────────────────────────────────────────────────────────────────

/** params: { team_id } or { project_id }; empty = personal scope. */
export const useBotModelsQuery = (params = {}, options = {}) =>
  useQuery({
    queryKey: botKeys.models(params),
    queryFn: () => unwrap(botsApi.models(params)),
    ...options,
  });

export const useBotsQuery = (teamId = null, options = {}) =>
  useQuery({
    queryKey: botKeys.list(teamId),
    queryFn: () => unwrap(botsApi.list(teamId)),
    ...options,
  });

export const useAISettingsQuery = (teamId = null, options = {}) =>
  useQuery({
    queryKey: botKeys.aiSettings(teamId),
    queryFn: () => unwrap(botsApi.getAISettings(teamId)),
    ...options,
  });

export const useProjectBotsQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.project(projectId),
    queryFn: () => unwrap(botsApi.projectBots(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useMyBotsQuery = (options = {}) =>
  useQuery({
    queryKey: botKeys.mine(),
    queryFn: () => unwrap(botsApi.mine()),
    ...options,
  });

// Without projectId: the bot's chats in every project, each with its project.
export const useBotChatsQuery = (botId, projectId = null, options = {}) =>
  useQuery({
    queryKey: botKeys.chats(botId, projectId),
    queryFn: () => unwrap(botsApi.chats(botId, projectId || undefined)),
    enabled: !!botId,
    ...options,
  });

export const useBotChatSearchQuery = (botId, projectId, q, options = {}) =>
  useQuery({
    queryKey: botKeys.chatSearch(botId, projectId, q),
    queryFn: () => unwrap(botsApi.chats(botId, projectId || undefined, q)),
    enabled: !!botId && q.length >= 2,
    placeholderData: (prev) => prev,
    ...options,
  });

// Every chat of the person, with every bot in every place (the Chats page), newest activity
// first, a page at a time. filters: q (2+ letters), bot_id, project_id ('none': in no project),
// needs_you. Each page also counts every chat that waits for the person (needs_you).
export const useAllChatsQuery = (filters = {}, options = {}) =>
  useInfiniteQuery({
    queryKey: botKeys.allChats(filters),
    queryFn: ({ pageParam }) => unwrap(botsApi.allChats({ ...filters, ...(pageParam ? { cursor: pageParam } : {}) })),
    initialPageParam: null,
    getNextPageParam: (last) => last?.next || undefined,
    placeholderData: filters.q ? keepPreviousData : undefined,
    refetchInterval: (query) => ((query.state.data?.pages || []).some((p) => (p.chats || []).some((c) => c.working)) ? 5000 : false),
    ...options,
  });

// How many chats the person has in each project and how many approvals wait there.
export const useChatCountsQuery = (options = {}) =>
  useQuery({
    queryKey: botKeys.chatCounts(),
    queryFn: () => unwrap(botsApi.chatCounts()),
    staleTime: 30_000,
    ...options,
  });

// A project's chats with every bot, newest first; each says what waits for the
// person, whether a bot works in it now and which schedule started it.
export const useProjectChatsQuery = (projectId, q = '', options = {}) =>
  useQuery({
    queryKey: botKeys.projectChats(projectId, q),
    queryFn: () => unwrap(botsApi.projectChats(projectId, q || undefined)),
    enabled: !!projectId && (!q || q.length >= 2),
    placeholderData: q ? (prev) => prev : undefined,
    refetchInterval: (query) => ((query.state.data?.chats || []).some((c) => c.working) ? 5000 : false),
    ...options,
  });

// Spotlight (pages/app/SpotlightPage.jsx): the sections are named in the person's language the
// first time it is opened. `marks`: which tasks are in it, for the mark on task rows.
export const spotlightDefaultNames = (t) => ({
  inbox: t('spotlight.sections.inbox'), upcoming: t('spotlight.sections.upcoming'),
  urgent: t('spotlight.sections.urgent'), in_progress: t('spotlight.sections.in_progress'),
  completed: t('spotlight.sections.completed'),
});
export const useSpotlightQuery = (names, options = {}) =>
  useQuery({
    queryKey: ['spotlight', 'view'],
    queryFn: () => unwrap(spotlightApi.open(names)),
    ...options,
  });
export const useSpotlightMarksQuery = (options = {}) =>
  useQuery({
    queryKey: ['spotlight', 'marks'],
    queryFn: () => unwrap(spotlightApi.marks()),
    staleTime: 60_000,
    ...options,
  });

export const useBotMemoryQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.memory(projectId),
    queryFn: () => unwrap(botsApi.memory(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useBotsActivityQuery = (options = {}) =>
  useQuery({
    queryKey: botKeys.activity(),
    queryFn: () => unwrap(botsApi.activity()),
    // Poll while something runs; finished runs also refresh it through the notification stream.
    refetchInterval: (q) => (q.state.data?.runs?.some((r) => ['queued', 'running'].includes(r.status)) ? 5000 : false),
    ...options,
  });

export const useProjectBotsPausedQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.paused(projectId),
    queryFn: () => unwrap(botsApi.projectPaused(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useServiceBotsPausedQuery = (options = {}) =>
  useQuery({ queryKey: botKeys.servicePaused(), queryFn: () => unwrap(adminApi.botsPause()), ...options });

export const useMyInstructionsQuery = (options = {}) =>
  useQuery({ queryKey: botKeys.myInstructions(), queryFn: () => unwrap(botsApi.myInstructions()), ...options });

export const useMyDefaultModelQuery = (options = {}) =>
  useQuery({ queryKey: botKeys.myDefaultModel(), queryFn: () => unwrap(botsApi.myDefaultModel()), ...options });

export const useAdminBotUsageQuery = (days, options = {}) =>
  useQuery({ queryKey: botKeys.adminUsage(days), queryFn: () => unwrap(adminApi.botsUsage(days)), ...options });

export const useByokQuery = (options = {}) =>
  useQuery({ queryKey: botKeys.byok(), queryFn: () => unwrap(botsApi.byok()), ...options });

export const useWorkspaceQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.workspace(projectId),
    queryFn: () => unwrap(botsApi.workspace(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useWorkspaceFilesQuery = (projectId, path, options = {}) =>
  useQuery({
    queryKey: botKeys.workspaceFiles(projectId, path),
    queryFn: () => unwrap(botsApi.workspaceFiles(projectId, path)),
    enabled: !!projectId,
    staleTime: 0,
    ...options,
  });

export const useBotSchedulesQuery = (botId, projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.schedules(botId, projectId),
    queryFn: () => unwrap(botsApi.schedules(botId, projectId)),
    enabled: !!botId && !!projectId,
    ...options,
  });

export const useAllSchedulesQuery = (options = {}) =>
  useQuery({
    queryKey: botKeys.allSchedules(),
    queryFn: () => unwrap(botsApi.allSchedules()),
    ...options,
  });

export const useScheduleRunsQuery = (id, options = {}) =>
  useQuery({
    queryKey: botKeys.scheduleRuns(id),
    queryFn: () => unwrap(botsApi.scheduleRuns(id)),
    enabled: !!id,
    ...options,
  });

export const useBotSecretsQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.secrets(projectId),
    queryFn: () => unwrap(botsApi.secrets(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useSkillHistoryQuery = (skillId, options = {}) =>
  useQuery({
    queryKey: botKeys.skillHistory(skillId),
    queryFn: () => unwrap(botsApi.skillHistory(skillId)),
    enabled: !!skillId,
    ...options,
  });

export const useBotProfileQuery = (options = {}) =>
  useQuery({
    queryKey: botKeys.profile(),
    queryFn: () => unwrap(botsApi.profile()),
    ...options,
  });

export const useBotLearningQuery = (projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.learning(projectId),
    queryFn: () => unwrap(botsApi.learning(projectId)),
    enabled: !!projectId,
    ...options,
  });

export const useBotCreditsQuery = (teamId, options = {}) =>
  useQuery({
    queryKey: botKeys.credits(teamId || null),
    queryFn: () => unwrap(botsApi.credits(teamId)),
    ...options,
  });

export const useBotConnectionsQuery = (botId, projectId, options = {}) =>
  useQuery({
    queryKey: botKeys.connections(botId, projectId),
    queryFn: () => unwrap(botsApi.connections(botId, projectId)),
    enabled: !!botId,
    ...options,
  });

export const useTaskBotStatusQuery = (taskId, options = {}) =>
  useQuery({
    queryKey: botKeys.taskStatus(taskId),
    queryFn: () => unwrap(botsApi.taskStatus(taskId)),
    enabled: !!taskId,
    staleTime: 0,
    ...options,
  });

export const useBotChatQuery = (chatId, options = {}) =>
  useQuery({
    queryKey: botKeys.chat(chatId),
    queryFn: () => unwrap(botsApi.chat(chatId)),
    enabled: !!chatId,
    staleTime: 0,
    ...options,
  });

export const useBotQuery = (id, options = {}) =>
  useQuery({
    queryKey: botKeys.detail(id),
    queryFn: () => unwrap(botsApi.get(id)),
    enabled: !!id,
    ...options,
  });
